import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, Phone, X, ArrowUpRight } from "lucide-react";
import { BUSINESS, SERVICES, CITIES } from "@/data/config";

const SIMPLE_NAV = [
  { label: "About", path: "/about" },
  { label: "Contact", path: "/contact" },
];

const MEGA_MENUS = {
  services: { label: "Services", items: SERVICES, base: "" },
  areas: { label: "Areas", items: CITIES, base: "" },
};

export default function Header() {
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(null); // 'services' | 'areas' | null
  const closeTimer = useRef(null);
  const navRef = useRef(null);
  const loc = useLocation();

  const openMega = (key) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setMenuOpen(key);
  };
  const scheduleCloseMega = () => {
    closeTimer.current = setTimeout(() => setMenuOpen(null), 150);
  };

  // Close mega menu on route change or Escape.
  useEffect(() => setMenuOpen(null), [loc.pathname]);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setMenuOpen(null);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
  // Click outside the nav (touch / keyboard users) closes an open mega menu.
  useEffect(() => {
    if (!menuOpen) return;
    const onClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) setMenuOpen(null);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [menuOpen]);

  // Lock the page scroll while the mobile menu is open so scrolling inside the
  // panel stays inside the panel, not the page underneath.
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (open) {
      const y = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${y}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.style.width = "100%";
      return () => {
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.left = "";
        document.body.style.right = "";
        document.body.style.width = "";
        window.scrollTo(0, y);
      };
    }
  }, [open]);

  return (
    <>
      <header
        data-testid="site-header"
        className="fixed top-0 inset-x-0 z-50 bg-[#0D0E12]/80 backdrop-blur-xl border-b border-white/5"
      >
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 h-[72px] md:h-[88px] flex items-center justify-between gap-6">
          <Link
            to="/"
            data-testid="header-logo"
            className="flex items-center gap-2 group"
            onClick={() => setOpen(false)}
          >
            <span className="w-8 h-8 flex items-center justify-center bg-copper-500 text-ink-950 font-display font-semibold text-lg leading-none rounded-sm">
              S
            </span>
            <span className="font-display text-xl md:text-2xl leading-none tracking-tight">
              Spokane <span className="text-copper-500">Gutter</span> Co.
            </span>
          </Link>

          <nav ref={navRef} className="hidden md:flex items-center gap-8 font-heading text-sm">
            {Object.entries(MEGA_MENUS).map(([key, menu]) => (
              <div
                key={key}
                className="relative"
                onMouseEnter={() => openMega(key)}
                onMouseLeave={scheduleCloseMega}
              >
                <button
                  type="button"
                  aria-haspopup="true"
                  aria-expanded={menuOpen === key}
                  data-testid={`nav-${key}-trigger`}
                  onClick={() => openMega(key)}
                  className="flex items-center gap-1.5 text-bone-200 hover:text-copper-500 transition-colors duration-200"
                >
                  {menu.label}
                  <ArrowUpRight
                    className={`w-3 h-3 transition-transform duration-200 ${menuOpen === key ? "rotate-90" : "rotate-45"}`}
                  />
                </button>
                <AnimatePresence>
                  {menuOpen === key && (
                    <motion.div
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                      data-testid={`${key}-megamenu`}
                      className={`absolute left-1/2 -translate-x-1/2 top-full mt-3 bg-[#14151b] border border-white/10 rounded-sm shadow-2xl p-3 z-50 ${
                        key === "areas" ? "w-[340px] grid grid-cols-2 gap-1" : "w-[300px]"
                      }`}
                    >
                      {menu.items.map((it) => (
                        <Link
                          key={it.slug}
                          to={`/${it.slug}`}
                          onClick={() => setMenuOpen(null)}
                          data-testid={`${key}-megamenu-link-${it.slug}`}
                          className="group flex items-center justify-between gap-2 px-3 py-2.5 rounded-sm text-bone-200 hover:text-copper-500 hover:bg-white/5 text-sm transition-colors duration-150"
                        >
                          {it.name}
                          <ArrowUpRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </Link>
                      ))}
                      <Link
                        to="/contact"
                        onClick={() => setMenuOpen(null)}
                        data-testid={`${key}-megamenu-quote-link`}
                        className={`flex items-center gap-2 px-3 py-2.5 mt-1 pt-3 border-t border-white/10 text-xs uppercase tracking-[0.18em] text-copper-500 font-heading ${
                          key === "areas" ? "col-span-2" : ""
                        }`}
                      >
                        Get a quote <ArrowUpRight className="w-3 h-3" />
                      </Link>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
            {SIMPLE_NAV.map((n) => (
              <Link
                key={n.path}
                to={n.path}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className="text-bone-200 hover:text-copper-500 transition-colors duration-200"
              >
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={BUSINESS.phoneHref}
              data-testid="header-phone-cta"
              className="hidden sm:inline-flex items-center gap-2 font-heading text-sm text-bone-50 hover:text-copper-500 transition-colors duration-200"
            >
              <Phone className="w-4 h-4" strokeWidth={2} />
              <span>{BUSINESS.phone}</span>
            </a>
            <Link
              to="/contact"
              data-testid="header-quote-cta"
              className="hidden md:inline-flex items-center gap-2 bg-copper-500 hover:bg-copper-600 text-ink-950 hover:text-white font-heading font-semibold text-sm px-5 py-3 rounded-sm transition-colors duration-200"
            >
              Free Quote
            </Link>
            <button
              type="button"
              aria-label="Open menu"
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="md:hidden text-bone-50 p-2"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Portal to document.body so the fixed panel is not trapped by the
          header's backdrop-filter containing block. */}
      {typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence>
            {open && (
              <motion.div
                key={loc.pathname}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: [0.76, 0, 0.24, 1] }}
                className="md:hidden fixed inset-x-0 top-[72px] bottom-0 bg-ink-950 border-t border-white/5 z-[60] overflow-y-auto overscroll-contain"
                data-testid="mobile-menu-panel"
                data-lenis-prevent
              >
                <motion.div
                  initial={{ y: -12 }}
                  animate={{ y: 0 }}
                  exit={{ y: -12 }}
                  transition={{ duration: 0.3, ease: [0.76, 0, 0.24, 1] }}
                  className="px-5 py-6 pb-24 space-y-5"
                >
                  {SIMPLE_NAV.map((n) => (
                    <Link
                      key={n.path}
                      to={n.path}
                      data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                      onClick={() => setOpen(false)}
                      className="block font-display text-3xl tracking-tight text-bone-50 hover:text-copper-500"
                    >
                      {n.label}
                    </Link>
                  ))}
                  <div className="pt-4 border-t border-white/5 space-y-3">
                    <div className="text-xs uppercase tracking-[0.24em] text-bone-400 font-heading">Services</div>
                    {SERVICES.map((s) => (
                      <Link
                        key={s.slug}
                        to={`/${s.slug}`}
                        onClick={() => setOpen(false)}
                        className="block text-bone-200 hover:text-copper-500 text-base"
                      >
                        {s.name}
                      </Link>
                    ))}
                  </div>
                  <div className="pt-4 border-t border-white/5 space-y-3">
                    <div className="text-xs uppercase tracking-[0.24em] text-bone-400 font-heading">Areas</div>
                    {CITIES.map((c) => (
                      <Link
                        key={c.slug}
                        to={`/${c.slug}`}
                        onClick={() => setOpen(false)}
                        className="block text-bone-200 hover:text-copper-500 text-base"
                      >
                        {c.name}
                      </Link>
                    ))}
                  </div>
                  <a
                    href={BUSINESS.phoneHref}
                    className="inline-flex items-center gap-2 bg-copper-500 text-ink-950 font-heading font-semibold text-sm px-5 py-3 mt-4 rounded-sm"
                  >
                    <Phone className="w-4 h-4" /> {BUSINESS.phone}
                  </a>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}

      {/* Mobile sticky action bar — always-visible dual CTA on small screens */}
      <div
        data-testid="mobile-action-bar"
        className="sm:hidden fixed bottom-0 inset-x-0 z-40 grid grid-cols-2 gap-px bg-white/10 border-t border-white/10 shadow-[0_-10px_30px_rgba(0,0,0,0.5)]"
      >
        <a
          href={BUSINESS.phoneHref}
          data-testid="mobile-bar-call"
          className="flex items-center justify-center gap-2 bg-ink-950 py-4 font-heading font-semibold text-sm text-bone-50"
        >
          <Phone className="w-4 h-4 text-copper-500" strokeWidth={2.5} />
          Call
        </a>
        <Link
          to="/contact"
          data-testid="mobile-bar-quote"
          className="flex items-center justify-center gap-2 bg-copper-500 py-4 font-heading font-semibold text-sm text-ink-950"
        >
          Free Quote
          <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
        </Link>
      </div>
    </>
  );
}
